import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoText from '@/components/atoms/InfoText';
import TitleText from '@/components/atoms/TitleText';
import ExpandableText from '@/components/atoms/ExpandableText';
import StarRating from '@/components/atoms/StarRating';
import { MilestoneFeedback } from '@/types/logisticsFeedback';
import { FeedbackProductRow, ProductRowPart } from './FeedbackProductRow';

const MILESTONE_ROW_TEST_IDS: Record<ProductRowPart, string> = {
  header: 'milestone-product-toggle',
  image: 'milestone-product-image',
  name: 'milestone-product-name',
  attributes: 'milestone-product-attr',
  qtyCol: 'milestone-product-qty-col',
  qtyLabel: 'milestone-product-qty-lbl',
  qtyValue: 'milestone-product-qty-val',
  priceCol: 'milestone-product-price-col',
  priceLabel: 'milestone-product-price-lbl',
  priceWrapper: 'milestone-product-price-wrapper',
  priceValue: 'milestone-product-price-val',
  currency: 'milestone-product-price-curr',
  toggle: 'milestone-product-dropdown',
  customization: 'milestone-product-customization',
};


interface FeedbackMilestonesListProps {
  milestones: MilestoneFeedback[];
  productsCount?: number;
  'data-testid'?: string;
}

export const FeedbackMilestonesList: React.FC<FeedbackMilestonesListProps> = ({
  milestones,
  productsCount,
  'data-testid': dataTestId = 'store-milestones-all-products',
}) => {
  const { t } = useTranslation();
  const calculatedCount =
    productsCount ??
    milestones.reduce((acc, m) => acc + m.products.length, 0);

  return (
    <div
      className="space-y-6 p-6 border rounded-2xl bg-light-surface-primary dark:bg-dark-surface-primary border-light-input-border dark:border-dark-input-border"
      data-testid={dataTestId}
    >
      {/* Header matching AllProducts.tsx */}
      <div className="flex items-center justify-between" data-testid={`${dataTestId}-header`}>
        <div>
          <TitleText
            text={t('logisticsFeedback.allProducts')}
            size="xl"
            className="!font-bold !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-title`}
          />
          <InfoText
            text={t('logisticsFeedback.allProductsDesc')}
            className="!text-text-secondary dark:!text-dark-text-secondary !text-sm mt-0.5"
            data-testid={`${dataTestId}-desc`}
          />
        </div>

        <div className="text-start flex flex-col justify-center gap-2.5" data-testid={`${dataTestId}-count-wrapper`}>
          <InfoText
            text={`${calculatedCount} ${t('logisticsFeedback.productUnit')}`}
            className="!font-bold !text-sm !leading-[100%] !text-light-information dark:!text-dark-information"
            data-testid={`${dataTestId}-count`}
          />
          <InfoText
            text={t('logisticsFeedback.totalInOrder')}
            className="!font-normal !text-sm !leading-[100%] !text-light-information dark:!text-dark-information"
            data-testid={`${dataTestId}-total-in-order`}
          />
        </div>
      </div>

      {/* Milestones list */}
      <div className="space-y-6 my-4" data-testid={`${dataTestId}-milestones`}>
        {milestones.map((milestone, mIdx) => (
          <div
            key={milestone.id || `milestone-${mIdx}`}
            className="space-y-4 pt-4 first:pt-0 border-t first:border-t-0 border-light-input-border dark:border-dark-input-border"
            data-testid={`milestone-box-${milestone.milestoneNumber}`}
          >
            <div className="flex items-center gap-x-3">
              <TitleText
                text={`${t('logisticsFeedback.milestone')} ${milestone.milestoneNumber}`}
                size="lg"
                className="!font-bold capitalize !text-light-primary dark:!text-dark-primary"
                data-testid={`milestone-title-${milestone.milestoneNumber}`}
              />
            </div>

            {/* Products inside this milestone */}
            <div
              className="space-y-4"
              data-testid={`milestone-products-container-${milestone.milestoneNumber}`}
            >
              {milestone.products.map((product, pIdx) => (
                <React.Fragment key={`${milestone.milestoneNumber}-${product.id}`}>
                  <div
                    className="space-y-3"
                    data-testid={`milestone-product-${milestone.milestoneNumber}-${pIdx}`}
                  >
                    <FeedbackProductRow
                      product={product}
                      getTestId={(part) =>
                        `${MILESTONE_ROW_TEST_IDS[part]}-${milestone.milestoneNumber}-${pIdx}`
                      }
                    />
                  </div>
                  {pIdx < milestone.products.length - 1 && (
                    <hr
                      className="border-light-input-border dark:border-dark-input-border"
                      key={`milestone-${milestone.milestoneNumber}-separator-${product.id}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Shipment review & shipment notes */}
            <div
              className="space-y-3 pt-2"
              data-testid={`milestone-shipment-review-${milestone.milestoneNumber}`}
            >
              <div className="flex items-center justify-between">
                <InfoText
                  text={t('logisticsFeedback.shipmentReview')}
                  className="!text-sm !font-normal !leading-none !tracking-normal !text-light-primary dark:!text-dark-primary"
                  data-testid={`milestone-shipment-review-label-${milestone.milestoneNumber}`}
                />
                <div
                  className="flex items-center gap-1.5"
                  data-testid={`milestone-shipment-rating-container-${milestone.milestoneNumber}`}
                >
                  <InfoText
                    text={
                      milestone.isReviewed !== false && milestone.shipmentReview.rating > 0
                        ? `(${milestone.shipmentReview.rating.toFixed(1)})`
                        : '(0)'
                    }
                    className="!text-sm !font-bold !text-light-primary dark:!text-dark-primary"
                    data-testid={`milestone-shipment-rating-value-${milestone.milestoneNumber}`}
                  />
                  <StarRating
                    value={milestone.shipmentReview.rating}
                    data-testid={`milestone-shipment-stars-${milestone.milestoneNumber}`}
                  />
                </div>
              </div>

              {milestone.shipmentReview.notes && (
                <div className="space-y-1">
                  <InfoText
                    text={t('logisticsFeedback.shipmentNotes')}
                    className="!text-sm !font-bold !text-light-primary dark:!text-dark-primary"
                    data-testid={`milestone-shipment-notes-label-${milestone.milestoneNumber}`}
                  />
                  <ExpandableText
                    text={milestone.shipmentReview.notes}
                    limit={140}
                    className="!text-sm !font-normal !leading-none !text-text-secondary dark:!text-dark-text-secondary"
                    data-testid={`milestone-shipment-notes-text-${milestone.milestoneNumber}`}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

